import React, { memo, useMemo } from 'react';

import { EventInfo, isTeamEvent } from '@/api/events/types/types';
import { ds } from '@/components/lib/design.system';
import { ScoreBoardAccordion } from '@/components/scoreboard/ScoreBoardAccordion';
import { ScoreboardTeamOverride, TeamLogos } from '@/components/scoreboard/types';
import { useScoreboard } from '@/components/scoreboard/use-scoreboard';
import { useEventTitle } from '@/feature/bets-sbk/hooks/use-event-title';
import { useSubscribeEventMatchUpdate } from '@/feature/event-details-sbk/hooks/use-subscribe-event-match-update';
import { Event } from '@/feature/event-details-sbk/types';
import { useLiveGamePeriod } from '@/feature/lobby/hooks/use-live-game-period';
import { useFetchOnResume } from '@/hooks/use-fetch-resume';
import { useGameTrackerConfig } from '@/hooks/use-game-tracker-config';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useEventStatus } from '@/hooks/use-sbk-event-details-cache';
import { getTeamOrder } from '@/utils/sport-utils';
import { getEventTeamLogos } from '@/utils/team-logo-colors';

export const PickemScoreBoardAccordion = memo(
    ({
        event,
        eventInfo,
        showGameTrackerTooltip,
        onViewFullEvent,
    }: {
        event: Event;
        eventInfo: EventInfo;
        showGameTrackerTooltip?: boolean;
        onViewFullEvent?: () => void;
    }) => {
        const eventStatus = useEventStatus(event.id) ?? event.status;
        const eventTitle = useEventTitle(event);
        const productFlags = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig?.settings);
        const defaultExpandedScoreboard = productFlags?.default_opened_scoreboard;

        // Pickem team assets stay the logo source for team events (header logos + grid rows - the
        // PAM event's logo_url is not reliably populated); individual events (tennis) have no
        // `teams`, so fall back to the PAM event's team data (logo_url is the country flag).
        const teamOverride: ScoreboardTeamOverride | undefined = useMemo(
            () =>
                isTeamEvent(eventInfo)
                    ? { icons: { away: eventInfo.teams[0].icon, home: eventInfo.teams[1].icon } }
                    : undefined,
            [eventInfo]
        );
        const scoreboardProps = useScoreboard(event, 'card', teamOverride);

        const [firstSide, secondSide] = getTeamOrder(event.sport.name);
        const teamsBySide = isTeamEvent(eventInfo) ? { away: eventInfo.teams[0], home: eventInfo.teams[1] } : undefined;
        const pickemTeamLogos: TeamLogos | undefined = teamsBySide
            ? [teamsBySide[firstSide], teamsBySide[secondSide]]
            : undefined;
        const { teamLogos, teamFallbackTextColors, shouldShowFallbackLogos } = pickemTeamLogos
            ? { teamLogos: pickemTeamLogos, teamFallbackTextColors: undefined, shouldShowFallbackLogos: undefined }
            : getEventTeamLogos(event);

        const periodLabel = useLiveGamePeriod(eventInfo);

        const gameTrackerConfig = useGameTrackerConfig(event.league, event.event_details);

        return (
            <ScoreBoardAccordion
                scoreboardProps={scoreboardProps}
                teamLogos={teamLogos}
                teamFallbackTextColors={teamFallbackTextColors}
                shouldShowFallbackLogos={shouldShowFallbackLogos}
                eventTitle={eventTitle}
                periodLabel={eventStatus === 'LIVE' ? periodLabel : undefined}
                backgroundColor={ds.colorToken.background.neutral.subtler}
                eventStatus={eventStatus}
                gameTrackerConfig={gameTrackerConfig}
                showGameTrackerTooltip={showGameTrackerTooltip}
                animateExplicitInitialExpansion={Boolean(defaultExpandedScoreboard)}
                accordionStyle={{ backgroundColor: ds.colorValue.background.neutral.subtler }}
                screenName={'Picks_EntryScreen_GameTrackerTooltip'}
                onViewFullEvent={onViewFullEvent}
            />
        );
    }
);

export const PickemScoreBoardWithSubscription = memo(
    ({
        event,
        eventInfo,
        refetch,
        showGameTrackerTooltip,
        onViewFullEvent,
    }: {
        event: Event;
        eventInfo: EventInfo;
        refetch: () => Promise<unknown>;
        showGameTrackerTooltip?: boolean;
        onViewFullEvent?: () => void;
    }) => {
        useSubscribeEventMatchUpdate(event.id);
        useFetchOnResume(refetch);

        return (
            <PickemScoreBoardAccordion
                eventInfo={eventInfo}
                event={event}
                showGameTrackerTooltip={showGameTrackerTooltip}
                onViewFullEvent={onViewFullEvent}
            />
        );
    }
);
